import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { IoMdArrowRoundBack, IoMdEye } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { useSignal } from '@preact/signals-react';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';

interface DocumentRecommendation {
    document: string;
    entityName: string;
    entityType: 'CPF' | 'CNPJ';
    recommendation: 'APPROVED' | 'REJECTED' | 'PENDING';
    creditFlag: boolean;
    triggeredRules: string[];
    provider: string;
    rawData: any;
}

const RecommendationEngineRecommendationView: React.FC = () => {
    const mapRecommendation = (backendRecommendation: string): 'APPROVED' | 'REJECTED' | 'PENDING' => {
        switch (backendRecommendation) {
            case 'APROVADO':
                return 'APPROVED';
            case 'REPROVADO':
                return 'REJECTED';
            case 'PENDENTE':
                return 'PENDING';
            default:
                return 'PENDING';
        }
    };

    const loadBatchDetails = async () => {
        setLoading(true);
        setError(null);
        setLoadingProgress(0);
        setLoadingMessage('Iniciando análise...');
        
        if (!id) {
            setError('ID do lote não informado');
            setLoading(false);
            return;
        }
        
        try {
            // Etapa 1: Carregando dados
            setLoadingProgress(10);
            setLoadingMessage('Carregando dados do lote...');
            
            // Callback de progresso
            const onProgress = (processed: number, total: number) => {
                // Progresso de 10% a 90% (reserva 10% para início e fim)
                const percentage = 10 + Math.round((processed / total) * 80);
                setLoadingProgress(percentage);
                setLoadingMessage(`Analisando: ${processed}/${total} documentos (${Math.round((processed / total) * 100)}%)`);
            };
            
            const data = await recommendationBFFService.analyzeBatch(id, onProgress);
            
            // Etapa final
            setLoadingProgress(95);
            setLoadingMessage('Finalizando análise...');
            
            setBatchData(data);
            setBatchDataRef(data);
            
            setLoadingProgress(100);
            setLoadingMessage('Concluído!');
            
            setLoading(false);
        } catch (err: any) {
            setError(err?.message || 'Erro ao carregar dados do lote');
            setLoading(false);
        }
    };
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();

    const cachedBatchData = location.state?.batchData; // Cache do estado de navegação
    const [batchData, setBatchData] = useState<any>(cachedBatchData || null);
    const [batchDataRef, setBatchDataRef] = useState<any>(null);
    const [loading, setLoading] = useState(!cachedBatchData); // Se tem cache, não carrega
    const [error, setError] = useState<string | null>(null);
    
    // Estados de progresso
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('Carregando dados do lote...');

    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<any[]>([]);

    useEffect(() => {
        if (id) {
            // Se já tem cache, não recarrega
            if (cachedBatchData) {
                console.log('✅ Usando cache ao voltar para RecommendationView');
                setBatchData(cachedBatchData);
                setLoading(false);
            } else {
                loadBatchDetails();
            }
        }
    }, [id]);

    const recommendations = useMemo(() => {
        if (!batchData || !batchData.reports || !Array.isArray(batchData.reports)) {
            return [];
        }
        const documentMap = new Map<string, DocumentRecommendation>();
        batchData.reports.forEach((report: any) => {
            const document = report.document || report.social_id || 'N/A';
            const entityType = report.backend_document_type || (report.document && report.document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ');
            if (!documentMap.has(document)) {
                documentMap.set(document, {
                    document: document,
                    entityName: report.entity_name || report.name || report.nome || 'Nome não disponível',
                    entityType: entityType,
                    recommendation: mapRecommendation(report.backend_recommendation),
                    creditFlag: report.backend_credit_flag || false,
                    triggeredRules: report.backend_reasons || [],
                    provider: batchData.provider || 'UNKNOWN',
                    rawData: report
                });
            } else {
                const existing = documentMap.get(document)!;
                // Merge triggered rules
                if (report.backend_reasons && Array.isArray(report.backend_reasons)) {
                    const existingRules = new Set(existing.triggeredRules);
                    report.backend_reasons.forEach((rule: string) => existingRules.add(rule));
                    existing.triggeredRules = Array.from(existingRules);
                }
                // Update recommendation
                const newRecommendation = mapRecommendation(report.backend_recommendation);
                if (newRecommendation === 'REJECTED') {
                    existing.recommendation = 'REJECTED';
                } else if (newRecommendation === 'PENDING' && existing.recommendation !== 'REJECTED') {
                    existing.recommendation = 'PENDING';
                }
                // Update credit flag
                if (report.backend_credit_flag) {
                    existing.creditFlag = true;
                }
            }
        });
        return Array.from(documentMap.values());
    }, [batchData]);

    useEffect(() => {
        headers.value = [
            { id: 'document', title: 'Documento', canSort: true },
            { id: 'entityName', title: 'Nome/Razão Social', canSort: true },
            { id: 'entityType', title: 'Tipo', canSort: true },
            { id: 'recommendation', title: 'Recomendação', canSort: true },
            { id: 'creditFlag', title: 'Sinalizado Crédito', canSort: true },
            { id: 'triggeredRules', title: 'Regras Ativadas', canSort: false },
            { id: 'actions', title: 'Ações', canSort: false }
        ];
        rows.value = recommendations.map((rec, index) => ({
            id: index,
            document: rec.document,
            entityName: rec.entityName,
            entityType: rec.entityType,
            recommendation: (
                <span className={`px-2 py-1 rounded text-sm font-medium ${getRecommendationStyle(rec.recommendation)}`}>
                    {getRecommendationLabel(rec.recommendation)}
                </span>
            ),
            creditFlag: (
                <span className={`px-2 py-1 rounded text-sm font-medium ${rec.creditFlag ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {rec.creditFlag ? 'Sim' : 'Não'}
                </span>
            ),
            triggeredRules: (
                <div className="max-w-xs">
                    {rec.triggeredRules.length > 0 ? (
                        <ul className="text-sm text-gray-600">
                            {rec.triggeredRules.slice(0, 3).map((rule, idx) => (
                                <li key={idx} className="truncate">• {rule}</li>
                            ))}
                            {rec.triggeredRules.length > 3 && (
                                <li className="text-gray-400">... +{rec.triggeredRules.length - 3} mais</li>
                            )}
                        </ul>
                    ) : (
                        <span className="text-gray-400 text-sm">Nenhuma regra ativada</span>
                    )}
                </div>
            ),
            actions: (
                <button
                    onClick={() => viewDocumentDetails(rec)}
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    title="Ver detalhes"
                    disabled={loading}
                >
                    <IoMdEye size={16} />
                    Detalhes
                </button>
            )
        }));
    }, [recommendations, loading]);

    const getRecommendationStyle = (recommendation: string): string => {
        switch (recommendation) {
            case 'APPROVED':
                return 'bg-green-100 text-green-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800';
            case 'PENDING':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getRecommendationLabel = (recommendation: string): string => {
        switch (recommendation) {
            case 'APPROVED':
                return 'Aprovado';
            case 'REJECTED':
                return 'Reprovado';
            case 'PENDING':
                return 'Pendente';
            default:
                return 'Desconhecido';
        }
    };

    const viewDocumentDetails = (recommendation: DocumentRecommendation) => {
        if (!batchData) {
            console.warn('[RecommendationEngineRecommendationView] batchData está nulo ao navegar para detalhes. Cache não será usado.');
        } else {
            console.info('[RecommendationEngineRecommendationView] batchData preenchido, cache será usado:', batchData);
        }
        
        const provider = batchData?.provider || 'UNKNOWN';
        console.log('📋 Visualizando detalhes do documento:', recommendation.document, 'Provider:', provider);
        
        // Usar grid genérica para todos os providers (colunas dinâmicas)
        // Passar documento via state ao invés de URL (evitar expor CNPJ/CPF)
        navigate(`/recommendation-engine/provider-documents/${id}`, {
            state: {
                document: recommendation.document,
                recommendation,
                batchData
            }
        });
    };

    const handleBack = () => {
        navigate('/recommendation-engine/recommendations/0/20/createdAt/DESC');
    };

    if (loading) {
        return (
            <PageForm
                pageTitle="Carregando Recomendações..."
                apiVersion="1.0"
                isLoading={false}
                breadcrumb={[]}
                controlSlot={<></>}
            >
                <div className="flex flex-col justify-center items-center h-64 space-y-6">
                    {/* Barra de progresso REAL */}
                    <div className="w-full max-w-md">
                        <div className="relative pt-1">
                            <div className="flex mb-2 items-center justify-between">
                                <div>
                                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                                        {loadingProgress}% concluído
                                    </span>
                                </div>
                            </div>
                            <div className="overflow-hidden h-3 mb-4 text-xs flex rounded-full bg-blue-100">
                                <div 
                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-300"
                                    style={{ width: `${loadingProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Ícone e mensagem */}
                    <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="text-lg text-gray-700 font-medium">{loadingMessage}</span>
                    </div>
                    
                    <p className="text-sm text-gray-500">Aguarde enquanto processamos os dados</p>
                </div>
            </PageForm>
        );
    }

    if (error) {
        return (
            <PageForm
                pageTitle="Erro ao Carregar Recomendações"
                apiVersion="1.0"
                isLoading={false}
                breadcrumb={[]}
                controlSlot={<></>}
            >
                <div className="text-center py-8">
                    <div className="text-red-600 mb-4">❌ {error}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </PageForm>
        );
    }

    return (
        <PageForm
            pageTitle={`Recomendações PLD - ${batchData?.identification || `Lote ${id}`}`}
            apiVersion="1.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Recommendation Engine', url: '/recommendation-engine' },
                { label: 'Recomendações', url: '#' }
            ]}
            controlSlot={<></>}
        >
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                    <IoMdArrowRoundBack />
                    Voltar para Lista de Lotes
                </button>

                {batchData && (
                    <div className="bg-gray-50 p-4 rounded-lg mb-6 mt-4">
                        <h3 className="font-semibold mb-2">Informações do Lote</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Provider:</span>
                                <div className="font-medium">{batchData.provider || 'N/A'}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Total de Documentos:</span>
                                <div className="font-medium">{batchData.reports?.length || 0}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Status:</span>
                                <div className="font-medium">{batchData.status || 'N/A'}</div>
                            </div>
                            <div>
                                <span className="text-gray-600">Processado em:</span>
                                <div className="font-medium">
                                    {batchData.createdAt || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ATable headers={headers.value}>
                {rows.value.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                        {headers.value.map((header) => (
                            <td key={header.id} className="px-4 py-2 border-b">
                                {row[header.id]}
                            </td>
                        ))}
                    </tr>
                ))}
            </ATable>
        </PageForm>
    );
};

export default RecommendationEngineRecommendationView;
